import React from 'react';
import { EditableText, PanelHeaderButton } from '~/design-system/components/v1_5/Layout';
import { cn } from '~/lib/utils';
import themes from '~/design-system/components/v1_5/Header/HeaderTheme.module.css';
import styles from './ImageCard.module.css';
import type { DirectoryItem, DirectoryItemDrag, HeaderTheme } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

export interface ImageCardProps {
  item: DirectoryItem;
  selected?: boolean;
  onClick?: () => void;
  /**
   * Omit and the card offers no `⋯` at all — which was every card until the per-kind menus landed,
   * and is still the drag overlay's copy.
   */
  onMoreClick?: () => void;
  menuOpen?: boolean;
  /** The filename is open for editing. Controlled by the panel, exactly as a row's name is. */
  editing?: boolean;
  onEditingChange?: (editing: boolean) => void;
  /** Fires only for a name that actually changed. Omit it and the card never opens for editing. */
  onRename?: (next: string) => void;
  theme?: HeaderTheme;
  /** Stories and visual tests only: paints the hover state without a real pointer. */
  forceHover?: boolean;
  /**
   * Drag-to-reorder wiring from `SortableDirectoryItem`. The measured box is the CELL and the
   * handle is the button inside it — the same split as a row, and for the same reason: the `⋯` is
   * a sibling of the handle, so pressing the menu can never lift the card.
   */
  drag?: DirectoryItemDrag;
  /** This is the lifted copy in the drag overlay rather than a card in the grid. */
  overlay?: boolean;
  className?: string;
}

/**
 * One image, as a card rather than a row.
 *
 * Images earn a grid — a filename in a row tells you nothing. `hero-storm.png` and `logo-wall.png`
 * are the same row twice; the pictures are not. So the leading slot grows until it IS the item and
 * the two lines of text drop underneath it, which is the one place in the panel where the list
 * stops being a list.
 */
export function ImageCard({
  item,
  selected = false,
  onClick,
  onMoreClick,
  menuOpen = false,
  editing = false,
  onEditingChange,
  onRename,
  theme: themeProp,
  forceHover = false,
  drag,
  overlay = false,
  className,
}: ImageCardProps) {
  const theme = useV15Theme(themeProp);
  const renameable = onRename !== undefined && (item.status ?? 'ready') === 'ready';
  const hasMenu = onMoreClick !== undefined && (item.status ?? 'ready') === 'ready' && !editing;

  const body = (
    <>
      <span
        className={styles.thumb}
        style={item.thumbnail ? ({ '--hv15-dir-thumb': item.thumbnail } as React.CSSProperties) : undefined}
        aria-hidden
      />
      <EditableText
        className={styles.name}
        value={item.title}
        editing={editing}
        onEditingChange={(next) => onEditingChange?.(next)}
        onCommit={(next) => onRename?.(next)}
        label={`Rename ${item.title}`}
      />
      <span className={styles.dims}>{item.meta}</span>
    </>
  );

  return (
    <div
      ref={drag?.ref}
      className={cn(
        themes[theme],
        styles.cell,
        forceHover && styles.forceHover,
        drag?.dragging && styles.dragging,
        overlay && styles.overlay,
        className,
      )}
      style={drag?.style}
      // The panel finds this cell to place the row menu against it, exactly as it finds a row.
      data-directory-row={item.id}
    >
      {editing ? (
        /* Not a button while the filename is a field — see `DirectoryRow`'s `Row` for the rule. */
        <div className={cn(styles.card, selected && styles.selected)}>{body}</div>
      ) : (
        <button
          type="button"
          ref={drag?.handleRef}
          {...drag?.handleProps}
          className={cn(styles.card, selected && styles.selected, forceHover && styles.forceHover)}
          aria-current={selected ? 'true' : undefined}
          onClick={onClick}
          // Two clicks land before this does, and selecting the same id twice is the same state —
          // the only reason the gesture can sit on top of a click-to-select card at all.
          onDoubleClick={renameable ? () => onEditingChange?.(true) : undefined}
        >
          {body}
        </button>
      )}

      {hasMenu ? (
        /* A sibling of the card button — a button inside a button is invalid markup and swallows
           the outer click. It floats over the picture's corner, so it sits on an overlay plate:
           a bare glyph over arbitrary imagery is illegible half the time. */
        <PanelHeaderButton
          icon="more"
          activeIcon="more-fill"
          active={menuOpen}
          hover="tone"
          label={`More actions for ${item.title}`}
          ariaHasPopup="menu"
          ariaExpanded={menuOpen}
          theme={theme}
          className={cn(styles.more, menuOpen && styles.moreOpen)}
          onClick={onMoreClick}
        />
      ) : null}
    </div>
  );
}
